/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useState } from 'react';
import { Check, Plus, Tag as TagIcon, X } from 'lucide-react';
import { ApiError, api } from '../lib/api';
import { MONTH_NAMES } from '../lib/desktopMascot';
import { errorText } from '../lib/errors';
import {
  CATEGORIES_MAX,
  CATEGORIES_SAVED_TEXT,
  CATEGORY_CHANGED_TEXT,
  CATEGORY_NAME_MAX,
  DAYS_IN_MONTH,
  DEFAULT_CATEGORY_WINDOW,
  categoriesChanged,
  categoriesRefusal,
  categoriesSaveBody,
  categoryLiveOn,
  categoryNameRefusal,
  categoryStatusText,
  categoryWindowText,
  freshCategoryId,
  monthDayNumber,
  monthDayParts,
  monthDayText,
  type CategoriesView,
  type CategoryWindow,
  type OutfitCategory,
} from '../lib/outfitCategories';
import { Button, Callout, Checkbox, EmptyState, Field, Icon, Input, Panel, Select, StatusChip, Tag } from '../ui';
import { plural } from '../ui/format';
import { SaveNote } from './SaveNote';

export interface OutfitCategoriesSectionProps {
  /** The registry the card loaded with the catalog; null while the service cannot read it. */
  view: CategoriesView | null;
  /** Writes are off: the admin is an auditor, or the card is busy. */
  locked: boolean;
  /** After a save: the card reads the catalog again, so every outfit row names its category as it now is. */
  onSaved: (view: CategoriesView) => void | Promise<void>;
}

const UNREADABLE_TEXT = 'Gyro could not read the outfit categories just now, so they cannot be changed here. Every outfit shows in people\'s galleries meanwhile. Reload in a minute.';

/** A category as the panel edits it: the service's fields, nothing more. */
function editable(category: OutfitCategory): OutfitCategory {
  return { id: category.id, name: category.name, enabled: category.enabled, window: category.window ? { from: category.window.from, to: category.window.to } : null };
}

/** One end of a season: a month and a day, each a select, with the day kept inside the month. */
function MonthDayFields({ idPrefix, label, value, disabled, onChange }: {
  idPrefix: string;
  label: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const { month, day } = monthDayParts(value);
  const days = DAYS_IN_MONTH[month - 1] ?? 31;
  return (
    <span className="ui-actions">
      <span className="ui-cell-sub">{label}</span>
      <Select
        size="sm"
        aria-label={`${label} month`}
        id={`${idPrefix}-month`}
        className="ui-w-auto"
        value={month}
        disabled={disabled}
        onChange={(e) => {
          const nextMonth = Number(e.target.value);
          onChange(monthDayText(nextMonth, Math.min(day, DAYS_IN_MONTH[nextMonth - 1] ?? 31)));
        }}
      >
        {MONTH_NAMES.map((name, index) => <option key={name} value={index + 1}>{name}</option>)}
      </Select>
      <Select
        size="sm"
        aria-label={`${label} day`}
        id={`${idPrefix}-day`}
        className="ui-w-auto"
        value={day}
        disabled={disabled}
        onChange={(e) => onChange(monthDayText(month, Number(e.target.value)))}
      >
        {/* February 29 means the end of February every year (the service holds a window through it, leap year or not). */}
        {Array.from({ length: days }, (_, index) => index + 1).map((option) => <option key={option} value={option}>{month === 2 && option === 29 ? '29 (end of February)' : option}</option>)}
      </Select>
    </span>
  );
}

/**
 * The Categories panel of the outfit gallery page (owner, 2026-10-09): every outfit category with its name, its switch
 * ("Shown"), its season (a yearly window of month-days that may run over the year end, such as September 1 to
 * February 28) and how many outfits it holds; a new category from a name; and one Save that sends the whole registry
 * with the revision the card loaded. Each outfit's own category is chosen on its row in the gallery (the card). A
 * category that is switched off or out of season hides its outfits from everyone until it is on again.
 */
export function OutfitCategoriesSection({ view, locked, onSaved }: OutfitCategoriesSectionProps) {
  const [rows, setRows] = useState<OutfitCategory[]>(() => view?.categories.map(editable) ?? []);
  const [loadedRevision, setLoadedRevision] = useState<number | null>(view?.revision ?? null);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  // A registry read again with another revision (the card's reload after a save, or another admin's change seen on a
  // reload) replaces the rows: the panel always edits what the service holds.
  useEffect(() => {
    if (!view || view.revision === loadedRevision) return;
    setRows(view.categories.map(editable));
    setLoadedRevision(view.revision);
  }, [view, loadedRevision]);

  const stored = view?.categories ?? [];
  const dirty = view !== null && categoriesChanged(rows, stored);
  const today = view ? monthDayNumber(view.today) : 0;
  const disabled = locked || busy || view === null;
  const nameTaken = newName.trim().length > 0 && rows.some((row) => row.name.trim().toLowerCase() === newName.trim().toLowerCase());
  const canAdd = !disabled && newName.trim().length > 0 && !nameTaken && rows.length < CATEGORIES_MAX && categoryNameRefusal(newName) === null;

  const update = (index: number, patch: Partial<OutfitCategory>) => {
    setRows((current) => current.map((row, at) => (at === index ? { ...row, ...patch } : row)));
  };
  const updateWindow = (index: number, patch: Partial<CategoryWindow>) => {
    setRows((current) => current.map((row, at) => (at === index && row.window ? { ...row, window: { ...row.window, ...patch } } : row)));
  };
  const add = () => {
    if (!canAdd) return;
    const name = newName.trim();
    const id = freshCategoryId(name, rows);
    if (!id) {
      setNote({ ok: false, text: 'Give the category a name with at least one letter or digit.' });
      return;
    }
    setRows((current) => [...current, { id, name, enabled: true, window: null }]);
    setNewName('');
    setNote(null);
  };
  const cancel = () => {
    setRows(stored.map(editable));
    setNote(null);
  };
  const save = async () => {
    if (!view) return;
    const refusal = categoriesRefusal(rows);
    if (refusal) {
      setNote({ ok: false, text: refusal });
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      const saved = await api.saveDesktopMascotCategories(categoriesSaveBody(rows, view.revision));
      setRows(saved.categories.map(editable));
      setLoadedRevision(saved.revision);
      setNote({ ok: true, text: CATEGORIES_SAVED_TEXT });
      await onSaved(saved);
    } catch (error) {
      const code = error instanceof ApiError && typeof error.body?.error === 'string' ? error.body.error : undefined;
      if (code === 'mascot_categories_changed') {
        setNote({ ok: false, text: CATEGORY_CHANGED_TEXT });
        try {
          const fresh = await api.desktopMascotCategories();
          setRows(fresh.categories.map(editable));
          setLoadedRevision(fresh.revision);
          await onSaved(fresh);
        } catch {
          // The note already says to reload.
        }
      } else {
        setNote({ ok: false, text: errorText(error, 'The categories were not saved.') });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      heading="h3"
      titleId="mascot-categories-title"
      title={<span className="ui-title-icon"><Icon icon={TagIcon} size="md" />Categories</span>}
      description="The sections people see outfits under, with General first; a category that is switched off or out of season hides its outfits until it is on again"
        actions={dirty ? <StatusChip tone="warn">Not saved yet</StatusChip> : null}
        footer={(
          <span>
            Group outfits so Gyro&apos;s outfits is not one long list: people see each category as a section they can open and close, with
            General first. A category that is switched off, or outside its season, hides its outfits from everyone until it is on
            again; people are back on the outfit they picked before, and their pick returns with the category. Day outfits keep
            their own day. Choose an outfit&apos;s category on its row above.
          </span>
        )}
      >
        {view === null ? (
          <Callout tone="warn" role="alert">{UNREADABLE_TEXT}</Callout>
        ) : (
          <fieldset disabled={disabled} className="ui-fieldset">
            <div className="ui-form">
              {rows.length === 0 && <EmptyState compact icon={TagIcon} title="No categories yet">Every outfit is in General.</EmptyState>}
              {rows.length > 0 && (
                <ul className="ui-cat-list">
                  {rows.map((row, index) => {
                    const live = categoryLiveOn(row, today);
                    const count = view.categories.find((known) => known.id === row.id)?.outfitCount ?? 0;
                    const status = categoryStatusText({ enabled: row.enabled, window: row.window, live });
                    return (
                      <li key={row.id} data-category={row.id} className="ui-cat-row">
                        <div className="ui-actions">
                          <Input
                            size="sm"
                            aria-label={`Name of the ${row.name || row.id} category`}
                            maxLength={CATEGORY_NAME_MAX}
                            value={row.name}
                            className="ui-w-md"
                            onChange={(e) => update(index, { name: e.target.value })}
                          />
                          <StatusChip tone={live ? 'ok' : row.enabled ? 'info' : 'neutral'}>{status}</StatusChip>
                          <span className="ui-cell-sub">{plural(count, 'outfit')}</span>
                          <Checkbox label="Shown" checked={row.enabled} onChange={(e) => update(index, { enabled: e.target.checked })} />
                          <Checkbox
                            label="Only part of the year"
                            checked={row.window !== null}
                            onChange={(e) => update(index, { window: e.target.checked ? { ...DEFAULT_CATEGORY_WINDOW } : null })}
                          />
                          <Button variant="ghost" size="sm" icon={X} aria-label={`Remove the ${row.name || row.id} category`} onClick={() => setRows((current) => current.filter((_, at) => at !== index))}>Remove</Button>
                        </div>
                        {row.window && (
                          <div className="ui-actions">
                            <MonthDayFields idPrefix={`mascot-category-${row.id}-from`} label="From" value={row.window.from} disabled={disabled} onChange={(from) => updateWindow(index, { from })} />
                            <MonthDayFields idPrefix={`mascot-category-${row.id}-to`} label="to" value={row.window.to} disabled={disabled} onChange={(to) => updateWindow(index, { to })} />
                            <span className="ui-cell-sub">{categoryWindowText(row.window)}{monthDayNumber(row.window.from) > monthDayNumber(row.window.to) ? ' (over the new year)' : ''}</span>
                          </div>
                        )}
                        <span className="ui-cell-sub">id <Tag>{row.id}</Tag></span>
                      </li>
                    );
                  })}
                </ul>
              )}
              <Field label="New category" error={nameTaken ? 'A category with that name is already listed.' : undefined}>
                <div className="ui-actions">
                  <Input
                    id="mascot-category-new"
                    size="sm"
                    placeholder="NFL"
                    maxLength={CATEGORY_NAME_MAX}
                    value={newName}
                    className="ui-w-md"
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                  />
                  <Button size="sm" icon={Plus} disabled={!canAdd} onClick={add}>Add category</Button>
                </div>
              </Field>
              <div className="ui-actions">
                <Button variant="primary" size="sm" icon={Check} busy={busy} disabled={!dirty || disabled} onClick={() => void save()}>Save categories</Button>
                <Button variant="ghost" size="sm" icon={X} disabled={!dirty || disabled} onClick={cancel}>Cancel</Button>
              </div>
            </div>
          </fieldset>
        )}
      <SaveNote note={note} />
    </Panel>
  );
}
